import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';

import LoadingPage from '../Components/LoadingPage';
import ErrorPage from '../ErrorPage';
import { useGlobalState } from '../GlobalState/GlobalState';
import NotFoundPage from '../NotFoundPage';
import { UserRole } from '../Utils/types';

const HomePage = React.lazy(() => import('../Views/HomePage/HomePage'));
const LoginPage = React.lazy(() => import('../Views/LoginPage/LoginPage'));
const MediaViewer = React.lazy(() => import('../Views/mediaViewer/MediaViewer'));
const CameraPage = React.lazy(() => import('../Views/Camera/CameraPage'));
const ProjectsContext = React.lazy(() => import('../Views/ProjectsPage/ProjectsContext'));
const ProjectsList = React.lazy(() => import('../Views/ProjectsPage/ProjectsList'));
const ProjectView = React.lazy(() => import('../Views/ProjectsPage/ProjectView'));
const DownloadPage = React.lazy(() => import('../Views/DownloadPage/DownloadPage'));
const SystemAdminPage = React.lazy(() =>
  import('../Views/SystemAdminPage/SystemAdminPage').then((m) => ({ default: m.SystemAdminPage }))
);
const DrawingsPage = React.lazy(() => import('../Views/DrawingsPage/DrawingsPage'));
const DrawingViewer = React.lazy(() => import('../Views/DrawingsPage/DrawingViewer'));
const SignupPage = React.lazy(() => import('../Views/SignupPage/SignupPage'));
const ForgotPasswordPage = React.lazy(() => import('../Views/LoginPage/ForgotPasswordPage'));
const NewPasswordPage = React.lazy(() => import('../Views/SignupPage/NewPasswordPage'));

export const router = createBrowserRouter([
  {
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/settings',
        element: (
          <RequireAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <SystemAdminPage />
            </React.Suspense>
          </RequireAuth>
        ),
        children: [
          {
            path: '/settings/:organisationId'
          }
        ]
      },
      {
        path: '/projects',

        element: (
          <RequireAdminAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <ProjectsContext />
            </React.Suspense>
          </RequireAdminAuth>
        ),
        children: [
          { path: '/projects', index: true, element: <ProjectsList /> },
          { path: '/projects/:projectId', element: <ProjectView /> }
        ]
      },
      // Drawings (OQ-161, flows/drawings.md §1, §5): admin-only, like the map.
      {
        path: '/drawings',
        element: (
          <RequireAdminAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <DrawingsPage />
            </React.Suspense>
          </RequireAdminAuth>
        )
      },
      {
        path: '/drawings/:drawingId',
        element: (
          <RequireAdminAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <DrawingViewer />
            </React.Suspense>
          </RequireAdminAuth>
        )
      },
      {
        path: '/login',
        element: (
          <React.Suspense fallback={<LoadingPage />}>
            <LoginPage />
          </React.Suspense>
        )
      },
      {
        path: '/signup',
        element: (
          <React.Suspense fallback={<LoadingPage />}>
            <SignupPage />
          </React.Suspense>
        )
      },
      {
        path: '/forgot-password',
        element: (
          <React.Suspense fallback={<LoadingPage />}>
            <ForgotPasswordPage />
          </React.Suspense>
        )
      },
      {
        path: '/reset-password',
        element: (
          <React.Suspense fallback={<LoadingPage />}>
            <NewPasswordPage />
          </React.Suspense>
        )
      },
      {
        path: '/',
        element: (
          <RequireAdminAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <HomePage />
            </React.Suspense>
          </RequireAdminAuth>
        )
      },
      {
        path: '/media/:docId',
        element: (
          <RequireAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <MediaViewer />
            </React.Suspense>
          </RequireAuth>
        )
      },
      {
        path: '/download/:urlId/:filetype/:filename',
        element: (
          <RequireAuth>
            <React.Suspense fallback={<LoadingPage />}>
              <DownloadPage />
            </React.Suspense>
          </RequireAuth>
        )
      },
      {
        path: '/camera/:linkId?',
        element: (
          <React.Suspense fallback={<LoadingPage />}>
            <CameraPage />
          </React.Suspense>
        )
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
]);

export const AppRoutes = () => <RouterProvider router={router} />;

function RequireAuth({ children }: { children: React.JSX.Element }) {
  const globalState = useGlobalState();
  const location = useLocation();

  if (!globalState.loading && !globalState.user) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  return children;
}

function RequireAdminAuth({ children }: { children: React.JSX.Element }) {
  const globalState = useGlobalState();
  const location = useLocation();

  if (!globalState.loading && !globalState.user) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  if (
    !globalState.loading &&
    globalState.user?.role !== UserRole.SUPERADMIN &&
    globalState.user?.role !== UserRole.ADMIN
  ) {
    // A USER's landing page is their own account (OQ-89).
    return <Navigate to='/settings' replace />;
  }

  return children;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function RequireSuperadminAuth({ children }: { children: React.JSX.Element }) {
  const globalState = useGlobalState();
  const location = useLocation();

  if (!globalState.loading && !globalState.user) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  if (!globalState.loading && globalState.user?.role !== UserRole.SUPERADMIN) {
    return <Navigate to='/' replace />;
  }

  return children;
}
